import { ChangeDetectorRef, Component, DoCheck, ElementRef, HostListener, Input, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import AclDialogView from './acl-dialog.component.html';
import './acl-dialog.component.css';

export interface AclSelectableEntity {
  id: number;
  label: string;
  initials?: string;
  sub?: string;
}

export interface AclDialogState {
  visible: boolean;
  savedVersion?: number;
}

interface AclEntry {
  id: string;
  subjectType: 'user' | 'group';
  subjectId: number;
  level: 'R' | 'W' | 'D';
}

const LEVELS: Array<{ value: 'R' | 'W' | 'D'; label: string; icon: string; desc: string }> = [
  { value: 'R', label: 'Lectura',   icon: 'fa-eye',    desc: 'Puede consultar' },
  { value: 'W', label: 'Escritura', icon: 'fa-pencil', desc: 'Consultar y editar' },
  { value: 'D', label: 'Borrado',   icon: 'fa-trash',  desc: 'Consultar, editar y borrar' },
];

const LEVEL_RANK: Record<string, number> = { R: 1, W: 2, D: 3 };

const ENTITY_KIND_LABELS: Record<string, { label: string; icon: string }> = {
  person:          { label: 'Persona',         icon: 'fa-user' },
  group:           { label: 'Grupo',           icon: 'fa-users' },
  event:           { label: 'Evento',          icon: 'fa-calendar' },
  document:        { label: 'Documento',       icon: 'fa-file-text-o' },
  mailing:         { label: 'Envío',           icon: 'fa-envelope' },
  correspondence:  { label: 'Correspondencia', icon: 'fa-exchange' },
  folder:          { label: 'Carpeta',         icon: 'fa-folder' },
};

@Component({
  selector: 'acl-dialog',
  template: AclDialogView,
  encapsulation: ViewEncapsulation.None
})
export class AclDialogComponent implements DoCheck, OnDestroy {
  @Input() title = '';
  @Input() entityKind = 'person';
  @Input() entity: { acl?: string | null } | null = null;
  @Input() state: AclDialogState | null = null;
  @Input() users: AclSelectableEntity[] = [];
  @Input() groups: AclSelectableEntity[] = [];
  @Input() readOnly = false;

  private cdr = inject(ChangeDetectorRef);

  entries: AclEntry[] = [];
  levels = LEVELS;

  draftType: 'user' | 'group' = 'user';
  draftQuery = '';
  showSuggest = false;
  filter = '';
  levelFilter: 'all' | 'R' | 'W' | 'D' = 'all';
  openPillId: string | null = null;
  recentlyAddedId: string | null = null;
  confirmClose = false;
  pillMenuStyle: { top: string; right: string } | null = null;
  suggestStyle: { position: string; top: string; left: string; width: string; zIndex: string } | null = null;

  @ViewChild('inputWrap') inputWrapRef!: ElementRef<HTMLElement>;

  private internalVisible = false;
  private lastVisible = false;
  private _uid = 0;
  private _recentlyAddedTimeout: ReturnType<typeof setTimeout> | null = null;
  private originalAcl = '';

  get visible(): boolean {
    return this.state?.visible ?? this.internalVisible;
  }

  set visible(value: boolean) {
    if (this.state) this.state.visible = value;
    else this.internalVisible = value;
  }

  get entityKindMeta(): { label: string; icon: string } {
    return ENTITY_KIND_LABELS[this.entityKind] || { label: 'Entidad', icon: 'fa-lock' };
  }

  get candidates(): AclSelectableEntity[] {
    const pool = this.draftType === 'group' ? this.groups : this.users;
    const usedIds = new Set(this.entries.filter(e => e.subjectType === this.draftType).map(e => e.subjectId));
    const q = this.draftQuery.toLowerCase();
    return pool
      .filter(p => !usedIds.has(p.id))
      .filter(p => !q || p.label.toLowerCase().includes(q) || (p.sub || '').toLowerCase().includes(q));
  }

  get visibleEntries(): AclEntry[] {
    return this.entries.filter(e => {
      if (this.levelFilter !== 'all' && e.level !== this.levelFilter) return false;
      if (!this.filter.trim()) return true;
      const s = this.getSubject(e);
      return s ? s.label.toLowerCase().includes(this.filter.toLowerCase()) : false;
    });
  }

  get groupEntries(): AclEntry[] {
    return this.visibleEntries.filter(e => e.subjectType === 'group');
  }

  get userEntries(): AclEntry[] {
    return this.visibleEntries.filter(e => e.subjectType === 'user');
  }

  get sums() {
    return {
      total: this.entries.length,
      r: this.entries.filter(e => e.level === 'R').length,
      w: this.entries.filter(e => e.level === 'W').length,
      d: this.entries.filter(e => e.level === 'D').length,
    };
  }

  get openPillEntry(): AclEntry | null {
    return this.openPillId ? (this.entries.find(e => e.id === this.openPillId) || null) : null;
  }

  get isDirty(): boolean {
    return this.serializeAcl(this.entries) !== this.originalAcl;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.openPillId) return;
    if ((event.target as HTMLElement).closest('.acl-dl-pill-wrap')) return;
    this.openPillId = null;
    this.pillMenuStyle = null;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this._recentlyAddedTimeout !== null) clearTimeout(this._recentlyAddedTimeout);
  }

  // DoCheck — necesario porque entity se pasa por referencia y OnChanges no detectaría mutaciones internas.
  // Acceptable en contexto híbrido Angular+AngularJS donde ngDoCheck corre en cada digest.
  // Se reinicializan entradas SÓLO cuando el diálogo abre (transición invisible→visible),
  // nunca por cambios externos en entity.acl mientras está abierto (evita resetear edits en curso).
  ngDoCheck(): void {
    const currentVisible = this.visible;

    if (currentVisible && !this.lastVisible) {
      this._uid = 0;
      this.entries = this.parseAcl(this.entity?.acl ?? '');
      this.originalAcl = this.serializeAcl(this.entries);
      this.draftQuery = '';
      this.filter = '';
      this.levelFilter = 'all';
      this.showSuggest = false;
      this.suggestStyle = null;
      this.openPillId = null;
      this.pillMenuStyle = null;
      this.recentlyAddedId = null;
      this.confirmClose = false;
    }

    this.lastVisible = currentVisible;
  }

  getSubject(entry: AclEntry): AclSelectableEntity | null {
    const list = entry.subjectType === 'group' ? this.groups : this.users;
    return list.find(s => s.id === entry.subjectId) || null;
  }

  levelMeta(value: string): typeof LEVELS[0] {
    return LEVELS.find(l => l.value === value) || LEVELS[0];
  }

  isImplied(granted: string, level: string): boolean {
    return LEVEL_RANK[granted] > LEVEL_RANK[level];
  }

  setDraftType(type: 'user' | 'group'): void {
    this.draftType = type;
    this.draftQuery = '';
  }

  addBySubject(subject: AclSelectableEntity): void {
    const newId = `acl${++this._uid}`;
    this.entries = [...this.entries, {
      id: newId,
      subjectType: this.draftType,
      subjectId: subject.id,
      level: 'R'
    }];
    this.draftQuery = '';
    this.showSuggest = false;
    this.recentlyAddedId = newId;
    if (this._recentlyAddedTimeout !== null) clearTimeout(this._recentlyAddedTimeout);
    this._recentlyAddedTimeout = setTimeout(() => {
      this._recentlyAddedTimeout = null;
      this.recentlyAddedId = null;
      this.cdr.markForCheck();
    }, 700);
    this.cdr.markForCheck();
  }

  removeEntry(id: string): void {
    this.entries = this.entries.filter(e => e.id !== id);
    this.cdr.markForCheck();
  }

  updateLevel(entry: AclEntry, level: 'R' | 'W' | 'D'): void {
    this.entries = this.entries.map(e => e.id === entry.id ? { ...e, level } : e);
    this.openPillId = null;
    this.cdr.markForCheck();
  }

  togglePill(id: string, event: Event): void {
    event.stopPropagation();
    if (this.openPillId === id) {
      this.openPillId = null;
      this.pillMenuStyle = null;
    } else {
      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      this.openPillId = id;
      // El pill menu se renderiza fuera de p-dialog (sin transform ancestor),
      // por lo que position:fixed usa coordenadas de viewport directamente.
      this.pillMenuStyle = {
        top: (rect.bottom + 4) + 'px',
        right: (window.innerWidth - rect.right) + 'px',
      };
    }
    this.cdr.markForCheck();
  }

  onInputFocus(): void {
    this.updateSuggestPosition();
    this.showSuggest = true;
  }

  onInputChange(): void {
    this.updateSuggestPosition();
    this.showSuggest = true;
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.showSuggest = false;
      this.suggestStyle = null;
    }, 250);
  }

  private updateSuggestPosition(): void {
    if (!this.inputWrapRef) return;
    const rect = this.inputWrapRef.nativeElement.getBoundingClientRect();
    this.suggestStyle = {
      position: 'fixed',
      top: (rect.bottom + 4) + 'px',
      left: rect.left + 'px',
      width: rect.width + 'px',
      zIndex: '99999',
    };
  }

  save(): void {
    if (this.readOnly) return;
    if (this.entity) this.entity.acl = this.serializeAcl(this.entries);
    if (this.state) this.state.savedVersion = Date.now();
    this.visible = false;
  }

  close(): void {
    if (this.isDirty) {
      this.confirmClose = true;
      this.cdr.markForCheck();
      return;
    }
    this._doClose();
  }

  discardAndClose(): void {
    this._doClose();
  }

  private _doClose(): void {
    this.visible = false;
    this.confirmClose = false;
    this.showSuggest = false;
    this.suggestStyle = null;
    this.openPillId = null;
    this.pillMenuStyle = null;
  }

  private parseAcl(acl: string): AclEntry[] {
    if (!acl?.trim()) return [];
    return acl.split(',')
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .map(raw => {
        const [subject, level] = raw.split(':');
        if (!subject || !level) return null;
        const prefix = subject.charAt(0);
        const id = parseInt(subject.slice(1), 10);
        const lvl = level.toUpperCase();
        if (!isFinite(id) || id <= 0 || !this.isValidLevel(lvl)) return null;
        if (prefix !== 'u' && prefix !== 'g') return null;
        return {
          id: `acl${++this._uid}`,
          subjectType: prefix === 'g' ? 'group' : 'user',
          subjectId: id,
          level: lvl
        } as AclEntry;
      })
      .filter((e): e is AclEntry => e !== null);
  }

  private serializeAcl(entries: AclEntry[]): string {
    const seen = new Set<string>();
    return entries
      .filter(e => {
        const key = `${e.subjectType}-${e.subjectId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return this.isValidLevel(e.level);
      })
      .map(e => `${e.subjectType === 'group' ? 'g' : 'u'}${e.subjectId}:${e.level}`)
      .join(',');
  }

  private isValidLevel(level: string): level is 'R' | 'W' | 'D' {
    return level === 'R' || level === 'W' || level === 'D';
  }
}
